/**
 * Market localization behaviour that Shopify's own form markup does not cover.
 *
 *  1. Keeps the buyer on the page they were reading when they switch country.
 *  2. Submits the footer's plain <select> without asking for a second click.
 *  3. Optionally moves a first-time visitor into the market that matches the
 *     region they are browsing from.
 *
 * Currency itself is never set directly: a market decides currency, pricing and
 * duties together, so the country is the only thing worth changing.
 */

/**
 * Records the buyer's country and, crucially, how it was decided.
 *
 * Stored as "manual:GB" or "auto:IN". The distinction matters: a country the
 * buyer picked is theirs and is never overridden, while one we assigned from
 * their IP is only ever a guess and should keep tracking where they actually
 * are. Without it, a shopper who once looked at prices in CAD stays in CAD
 * forever, which reads as the store being broken rather than obedient.
 */
const CHOICE_KEY = 'ch:market-country';

/** How long an auto-assigned country is trusted before its region is re-checked. */
const AUTO_TTL_MS = 24 * 60 * 60 * 1000;

/** Shopify only accepts localization changes through this endpoint. */
const LOCALIZATION_ACTION = '/localization';

/**
 * @param {string} key
 * @returns {string | null}
 */
function readStorage(key) {
  try {
    return localStorage.getItem(key);
  } catch {
    // Private mode, or storage disabled. Treat as "nothing remembered".
    return null;
  }
}

/**
 * @param {string} key
 * @param {string} value
 */
function writeStorage(key, value) {
  try {
    localStorage.setItem(key, value);
  } catch {
    // Nothing to do — the visitor simply gets asked again next time.
  }
}

/**
 * Submits a country change to Shopify.
 *
 * Built from scratch rather than reusing whichever form happens to be on the
 * page: the auto-switch has to work even when the merchant has turned the
 * switchers off, and it should not disturb a form the buyer can see.
 *
 * @param {string} countryCode
 */
function submitCountry(countryCode) {
  const root = window.Shopify?.routes?.root ?? '/';
  const form = document.createElement('form');

  form.method = 'post';
  form.action = `${root}localization`;
  form.hidden = true;

  const fields = {
    form_type: 'localization',
    utf8: '✓',
    _method: 'put',
    country_code: countryCode,
    return_to: location.pathname + location.search,
  };

  for (const [name, value] of Object.entries(fields)) {
    const input = document.createElement('input');
    input.type = 'hidden';
    input.name = name;
    input.value = value;
    form.appendChild(input);
  }

  document.body.appendChild(form);
  form.submit();
}

/**
 * Shopify redirects to `return_to` after switching, falling back to the referer.
 * The referer loses the query string, which on a filtered collection means the
 * buyer lands somewhere they did not ask for. Pin it to the current URL instead.
 *
 * @param {HTMLFormElement} form
 */
function pinReturnTo(form) {
  const returnTo = location.pathname + location.search;
  const existing = form.querySelector('input[name="return_to"]');

  if (existing instanceof HTMLInputElement) {
    existing.value = returnTo;
    return;
  }

  const input = document.createElement('input');
  input.type = 'hidden';
  input.name = 'return_to';
  input.value = returnTo;
  form.appendChild(input);
}

/**
 * @param {EventTarget | null} node
 * @returns {HTMLFormElement | null}
 */
function localizationFormFor(node) {
  if (!(node instanceof Element)) return null;

  const form = node.closest(`form[action$="${LOCALIZATION_ACTION}"]`);
  return form instanceof HTMLFormElement ? form : null;
}

/**
 * The header dropdown hands off to the shared localization component, which
 * calls `form.submit()` directly. That bypasses submit listeners entirely, so
 * the buyer's choice has to be caught at the click instead. Capture phase, so it
 * lands before the component submits and navigates away.
 */
document.addEventListener(
  'click',
  (event) => {
    const item = event.target instanceof Element ? event.target.closest('[data-value]') : null;
    const form = localizationFormFor(item);
    if (!item || !form) return;

    const chosen = item.getAttribute('data-value');
    if (chosen) writeStorage(CHOICE_KEY, 'manual:' + chosen);
    pinReturnTo(form);
  },
  true
);

/**
 * The footer selector is a bare <select> rather than the header's dropdown, so
 * it needs to submit itself. Without this the buyer picks a country and nothing
 * happens, which reads as "the currency doesn't change".
 */
document.addEventListener('change', (event) => {
  const select = event.target;
  if (!(select instanceof HTMLSelectElement) || select.name !== 'country_code') return;

  const form = localizationFormFor(select);
  if (!form) return;

  writeStorage(CHOICE_KEY, 'manual:' + select.value);
  pinReturnTo(form);
  form.submit();
});

/**
 * Language changes submit through the same component, and so need the same
 * return-to pinning. The chosen country is untouched, so nothing is remembered.
 */
document.addEventListener('change', (event) => {
  const select = event.target;
  if (!(select instanceof HTMLSelectElement) || select.name !== 'language_code') return;

  const form = localizationFormFor(select);
  if (form) pinReturnTo(form);
});

/**
 * Covers the no-component path: a native submit from the <noscript> button, or
 * any form added after this module ran.
 */
document.addEventListener(
  'submit',
  (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement) || !form.action.endsWith(LOCALIZATION_ACTION)) return;

    const country = form.querySelector('[name="country_code"]');
    if (country instanceof HTMLInputElement || country instanceof HTMLSelectElement) {
      writeStorage(CHOICE_KEY, 'manual:' + country.value);
    }

    pinReturnTo(form);
  },
  true
);

/**
 * Moves a first-time visitor into the market matching where they are browsing
 * from, using Shopify's own IP and browser-language detection.
 *
 * Only ever fires once per visitor. GeoIP is wrong often enough — VPNs, corporate
 * proxies, people shopping for delivery somewhere else — that repeatedly pulling
 * someone back to a "correct" market would be worse than leaving them alone.
 *
 * @element custom-region-detect
 * @attr {string} data-current - ISO code of the country currently active
 * @attr {string} data-available - Comma separated ISO codes the store sells to
 */
class CustomRegionDetect extends HTMLElement {
  connectedCallback() {
    // Auto-switching mid-edit would fight the merchant in the theme editor.
    if (window.Shopify?.designMode) return;

    const stored = readStorage(CHOICE_KEY) ?? '';

    // A country the buyer picked is theirs. Never second-guess it.
    if (stored.startsWith('manual:')) return;

    // An auto-assigned country is only a guess, so it is re-checked once a day.
    // Someone who was placed in CAD on a trip should not still be in CAD at home.
    if (stored.startsWith('auto:')) {
      const at = Number(stored.split(':')[2] ?? 0);
      if (Number.isFinite(at) && Date.now() - at < AUTO_TTL_MS) return;
    } else if (stored) {
      // Written by an older version of this script, before intent was recorded.
      // Treat it as a guess rather than stranding the visitor on it forever.
    }

    this.#detect();
  }

  async #detect() {
    const current = this.dataset.current ?? '';
    const available = (this.dataset.available ?? '').split(',').filter(Boolean);

    // Whatever happens next, this visitor has now been placed. Recorded up front,
    // stamped with the time, so a failed or inconclusive lookup cannot retry on
    // every page view — it waits out the TTL like any other auto assignment.
    const stamp = (code) => writeStorage(CHOICE_KEY, `auto:${code}:${Date.now()}`);
    stamp(current);

    let detected;

    try {
      const root = window.Shopify?.routes?.root ?? '/';
      const response = await fetch(
        `${root}browsing_context_suggestions.json?country[enabled]=true&country[exclude]=${encodeURIComponent(current)}`
      );

      if (!response.ok) return;

      const data = await response.json();
      detected = data?.detected_values?.country?.handle;
    } catch {
      // Offline, blocked, or the endpoint is unavailable. Leave the buyer be.
      return;
    }

    // Nothing detected, already right, or somewhere this store does not sell to.
    if (!detected || detected === current || !available.includes(detected)) return;

    stamp(detected);
    submitCountry(detected);
  }
}

if (!customElements.get('custom-region-detect')) {
  customElements.define('custom-region-detect', CustomRegionDetect);
}
